import { forms, isForbidden } from '@cwos/api-client';

/**
 * The response reader's decisions, out of the component so they can be asserted
 * without mounting anything.
 *
 * ===========================================================================
 * A RESPONSE IS EVIDENCE, SO IT IS READ AGAINST THE VERSION IT ANSWERED
 * ===========================================================================
 * `GET /api/forms/responses/:id` returns a `version` alongside the response,
 * and it is not "the current version of this form" — the server resolves it
 * through the response's own `formVersionId`, which is the whole point of
 * `contract.ts` rule 4. This module never takes a version from anywhere else,
 * and {@link answersAgainstVersion} orders and labels the answers from THAT
 * version's field list. A screen that fetched `forms.detail(definitionId)` and
 * rendered `publishedVersion(...)` beside these answers would relabel last
 * year's evidence with this year's questions, and nothing on the wire would
 * complain.
 *
 * ===========================================================================
 * FIVE STATES, AND THE FIFTH IS THE ONE THAT MATTERS
 * ===========================================================================
 * The four this codebase already insists on are loading / ready / refused /
 * failed, and `listState.ts` gives them for lists. A response DETAIL has a
 * fifth that a list does not, and collapsing it is the defect this file exists
 * to prevent:
 *
 *   403  REFUSED — this account does not hold `forms:read_responses`. It may
 *        not use the door at all. An answer, never a retry.
 *   404  WITHHELD-OR-ABSENT — the door opened and this row did not answer. The
 *        server WILL NOT SAY WHICH, deliberately: `response-reach.ts` returns
 *        the same constant message for `withheld` and for `absent`, because
 *        distinguishing them would tell a caller trying uuids which ones this
 *        organization has. So the copy names both worlds and picks neither.
 *
 * "This response was deleted" is the sentence that must never appear, and
 * "there are no responses" is the list-shaped version of the same lie. Six
 * backend places in this programme have already conflated withheld with empty;
 * a supervisor reading "not there" where the truth is "not yours" is that same
 * defect wearing a UI.
 *
 * ===========================================================================
 * WHAT THE SCREEN MAY SAY ABOUT SCOPE, AND WHAT IT MAY NOT
 * ===========================================================================
 * It may say what the READER'S OWN KEYS are, because those are on this side of
 * the wire. It may NOT say which rows answer: that is decided per row, at read
 * time, by `formResponseReachMap` against the response's `subject_client_id`
 * and the caller's open assignments, and neither input is here. So
 * {@link responseScopeSentence} states the reader's own reach and stops.
 */

// ---------------------------------------------------------------- the phases

/**
 * What the response reader should be showing.
 *
 * `idle` is not a failure and not an empty: the reader has not named a response
 * yet, so nothing has been asked and nothing may be claimed.
 */
export type ResponsePhase =
  /** Nothing asked for yet. */
  | { kind: 'idle' }
  | { kind: 'loading' }
  /** 403. This account may not use this door. */
  | { kind: 'refused' }
  /**
   * 404. Either this organization has no such response, or it has one and it is
   * not this reader's to see — and the server refuses to say which, on purpose.
   */
  | { kind: 'withheld-or-absent' }
  | { kind: 'failed'; error?: unknown }
  | { kind: 'ready' };

/** The parts of a react-query result this decision needs. */
export interface ResponseQueryLike {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: unknown;
}

/** The status a rejected request carried, structurally — never `instanceof`. */
export function statusOf(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' && Number.isFinite(status) ? status : null;
}

/**
 * @param query   the react-query result
 * @param asked   false when the reader has not named a response yet
 *
 * ORDER IS LOAD-BEARING. 403 is tested before 404 and both before the generic
 * failure, and `withheld-or-absent` is tested before `failed` because a screen
 * that offered "try again" for a 404 would be promising that asking a second
 * time changes an answer the server has already given deliberately.
 */
export function responsePhase(query: ResponseQueryLike, asked: boolean): ResponsePhase {
  if (!asked) return { kind: 'idle' };
  if (query.isError) {
    // `isForbidden` first — it is the api-client's own class test, and the
    // status check below is the structural backstop for a transport whose
    // errors are not that class.
    if (isForbidden(query.error) || statusOf(query.error) === 403) return { kind: 'refused' };
    if (statusOf(query.error) === 404) return { kind: 'withheld-or-absent' };
    if (query.data === undefined) return { kind: 'failed', error: query.error };
  }
  if (query.data !== undefined) return { kind: 'ready' };
  // Not errored, no payload: either in flight, or react-query's answer for a
  // query that has not been enabled. Both are "we have not been told", and the
  // honest rendering of that is the pending one — never an empty result.
  return { kind: 'loading' };
}

/**
 * The sentence for a 404 on this route.
 *
 * TWO WORLDS, ONE SENTENCE, and it names both rather than picking the one that
 * reads more tidily. It quotes nothing from the server: the refusal body is the
 * constant string `response not found`, which is safe but is not an
 * explanation, and an id is not an explanation either.
 */
export const WITHHELD_OR_ABSENT_HEADING = 'No answer for that response';
export const WITHHELD_OR_ABSENT_MESSAGE =
  'Either this organization has no response with that id, or it has one and it is not yours to ' +
  'read. Phibian will not say which — telling you apart would let anyone with a list of ids ' +
  'discover which ones this organization holds. Do not read this as "it was deleted".';

/** The sentence for a 403 on this route. An answer about the account. */
export const RESPONSE_REFUSED_HEADING = 'Not yours to read';
export const RESPONSE_REFUSED_MESSAGE =
  'Your account does not hold the permission to read form responses. That is a decision your ' +
  'organization controls, and asking again will not change it. Being able to SUBMIT a form is a ' +
  'different permission and does not include reading one back.';

// ------------------------------------------------------------------- scope

/**
 * How far this reader's OWN keys reach, in words, from their own permission set.
 *
 * The conjunction lives in `forms.readsEveryResponse` — the client-side mirror
 * of `formResponseReader` — and not in an `&&` here, so that widening it is one
 * edit in one file on each side of the wire rather than a hunt.
 */
export type ResponseReachScope =
  { kind: 'none' } | { kind: 'own-and-caseload' } | { kind: 'organization' };

export function responseReachScope(has: (key: string) => boolean): ResponseReachScope {
  if (!has(forms.FORMS_READ_RESPONSES)) return { kind: 'none' };
  return forms.readsEveryResponse(has) ? { kind: 'organization' } : { kind: 'own-and-caseload' };
}

export function responseScopeSentence(scope: ResponseReachScope): string {
  switch (scope.kind) {
    case 'none':
      return RESPONSE_REFUSED_MESSAGE;
    case 'own-and-caseload':
      // Deliberately does NOT say "you supervise". The server's edge is an OPEN
      // worker_client_assignment reached through your own visible workers, plus
      // the responses you yourself started or submitted — and a supervisor
      // cannot read a check-in about one of their own workers unless they wrote
      // it, because a worker-subject response has no participant to authorize
      // against. Saying "your team's" would promise that.
      return 'the responses you started or submitted yourself, and those about a participant on your caseload';
    case 'organization':
      return 'every response in this organization';
  }
}

/**
 * Why the reader holding the widener alone still sees a caseload.
 *
 * Shown only to somebody who holds `forms:read_responses_all` WITHOUT
 * `directory:read_all`, because for them the screen would otherwise look
 * broken: they were granted an organization-wide key and are not getting an
 * organization-wide answer. They are not — the server's conjunction is refusing
 * it — and that is a fact worth stating rather than leaving as a mystery.
 */
export function widenerWithoutDirectoryNote(has: (key: string) => boolean): string | null {
  if (!has(forms.FORMS_READ_RESPONSES_ALL)) return null;
  if (has(forms.FORMS_DIRECTORY_READ_ALL)) return null;
  return (
    'Your account holds the organization-wide form-response permission but not organization-wide ' +
    'access to the workforce directory, and Phibian requires both. Reading every response is ' +
    'reading every participant, so the wider key does nothing on its own — you are reading at ' +
    'caseload reach.'
  );
}

// ------------------------------------------------------- version currency

/**
 * WHAT THE READER IS LOOKING AT, in version terms.
 *
 * A published version is immutable and a response is welded to the one that
 * collected it. So the only honest thing a reader can be told is: this is the
 * wording this person was actually shown, and here is what has happened to that
 * wording since. The screen deliberately does not go and ask what the form's
 * CURRENT version is — that number would be true about the form and false about
 * this evidence, and putting the two side by side invites the substitution.
 */
export type VersionCurrency =
  | { kind: 'in-use'; versionNumber: number }
  | { kind: 'retired'; versionNumber: number; retiredAt: string }
  | { kind: 'never-published'; versionNumber: number; state: forms.FormVersionState };

export function versionCurrency(version: forms.FormVersionView): VersionCurrency {
  if (version.retiredAt !== null) {
    return { kind: 'retired', versionNumber: version.versionNumber, retiredAt: version.retiredAt };
  }
  if (version.state === 'published') {
    return { kind: 'in-use', versionNumber: version.versionNumber };
  }
  // A response against a version that was never published is not a state the
  // lifecycle produces through the UI, and it is not this screen's job to
  // pretend it cannot arrive. Saying so is better than showing a "Published"
  // badge on evidence that was never published.
  return { kind: 'never-published', versionNumber: version.versionNumber, state: version.state };
}

export function versionCurrencySentence(currency: VersionCurrency): string {
  switch (currency.kind) {
    case 'in-use':
      return `These answers were given against v${currency.versionNumber}, which is still the published wording. A published version cannot be edited — changing a question makes a new version, so this evidence keeps its meaning.`;
    case 'retired':
      return `These answers were given against v${currency.versionNumber}, which has since been retired. You are reading the wording this person was actually shown, not the wording in use today. A published version is never edited in place, so nothing here has drifted.`;
    case 'never-published':
      return `These answers were given against v${currency.versionNumber}, which is recorded as ${currency.state} rather than published. That is unusual and worth checking — it is reported rather than dressed up as a published version.`;
  }
}

// -------------------------------------------------------------- the answers

/**
 * One row of the response, joined to the version's own field.
 *
 * `unknownField: true` is the arm that must exist. `form_answer.field_key` is a
 * foreign key into this version's field list, so an answer with no field should
 * be impossible — and a renderer that silently dropped it would turn an
 * impossible state into a missing row, which is how evidence goes quiet. It is
 * shown, labelled by its own key, and marked.
 */
export interface AnswerRow {
  readonly fieldKey: string;
  readonly label: string;
  readonly sectionTitle: string;
  readonly dataType: forms.FormFieldType;
  readonly required: boolean;
  readonly answer: forms.FormAnswerView | undefined;
  readonly unknownField: boolean;
}

/**
 * The response's answers IN THE VERSION'S OWN ORDER, labelled by the version's
 * own labels.
 *
 * NOT the answer's `label` field for ordering, and not the order the answers
 * arrived in. The version is the schema the response was collected under; using
 * it as the schema for reading the response back is what "welded to the version
 * that collected it" means in a renderer.
 *
 * A field with no answer is a row with `answer: undefined` — present, so the
 * reader can see that the question was asked and left blank, which is a fact
 * about the evidence and disappears entirely if unanswered fields are filtered
 * out.
 */
export function answersAgainstVersion(
  version: forms.FormVersionView,
  answers: readonly forms.FormAnswerView[],
): AnswerRow[] {
  const byKey = new Map(answers.map((a) => [a.fieldKey, a]));
  const rows: AnswerRow[] = [];
  const ordered = [...version.fields].sort(
    (a, b) => a.sectionOrder - b.sectionOrder || a.fieldOrder - b.fieldOrder,
  );
  for (const field of ordered) {
    rows.push({
      fieldKey: field.fieldKey,
      label: field.label,
      sectionTitle: field.sectionTitle,
      dataType: field.dataType,
      required: field.required,
      answer: byKey.get(field.fieldKey),
      unknownField: false,
    });
    byKey.delete(field.fieldKey);
  }
  for (const [fieldKey, answer] of byKey) {
    rows.push({
      fieldKey,
      label: answer.label,
      sectionTitle: 'Not in this version',
      dataType: answer.dataType,
      required: false,
      answer,
      unknownField: true,
    });
  }
  return rows;
}

/**
 * An answer's value as text.
 *
 * PLAIN TEXT ONLY, ALWAYS. A form definition and its answers are tenant content
 * — a customer wrote the labels and a worker typed the prose — so nothing here
 * ever produces markup, and no caller of this may pass the result to anything
 * that interprets one. React escapes what this returns; the rule is written
 * down because the temptation on a "rich" answer type is real.
 *
 * `null` is NOT rendered as an empty string: an unanswered question and a
 * question answered with the empty string are different facts about evidence.
 */
export function answerText(answer: forms.FormAnswerView | undefined): string {
  if (answer === undefined) return 'Not answered';
  const value = answer.value;
  if (value === null) return 'Left blank';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.length === 0 ? 'Nothing selected' : value.join(', ');
  }
  return value === '' ? 'Left blank' : String(value);
}

/**
 * Whether this response was attested, and by whom.
 *
 * Three arms and not two. `requiresAttestation` on the VERSION says whether the
 * instrument asked for one; `attestedBy` on the RESPONSE says whether one was
 * given. "Not required" and "required and missing" are different findings and an
 * auditor cares about exactly the second.
 */
export type AttestationReading =
  | { kind: 'not-required' }
  | { kind: 'given'; by: string; statement: string | null; at: string | null }
  | { kind: 'missing' };

/**
 * TAKES THE TWO PARTS, NOT THE WHOLE `FormResponseDetail`, and the reason is
 * not style.
 *
 * `apps/api/test/unit/client-binding-callers.test.ts` resolves every property
 * access whose symbol is declared in `@cwos/api-client` and credits
 * `<namespace>.<name>` with a call site. `forms.ts` exports BOTH a request
 * binding named `version` and an interface property named `version`, so writing
 * `detail.version` here made the census record `forms.version` — a route
 * binding this lane never calls — as reached. That is the census's own
 * "reachable because a type was mentioned" failure mode, one layer up, and the
 * cheapest honest fix is not to write the access.
 */
export function attestationReading(
  version: forms.FormVersionView,
  response: forms.FormResponseView,
): AttestationReading {
  if (!version.requiresAttestation) return { kind: 'not-required' };
  const by = response.attestedBy;
  if (by === null) return { kind: 'missing' };
  return {
    kind: 'given',
    by: by.displayName,
    statement: response.attestationStatement,
    at: response.attestedAt,
  };
}
